import { Box, Flex, Modal, Radio } from '@mantine/core';
import { GetCurrentUserResponse } from 'api/actions/get-current-user/get-current-user-response';
import React from 'react';
import theme from 'theme';
import useLang from 'lang';
import { HeaderIcon } from 'components/icons/HeaderIcon';
import P1SemiBold from 'theme/components/Typography/P1SemiBold';

export type SelectableBuilding = NonNullable<GetCurrentUserResponse['buildings']>[number];

interface BuildingSelectorMenuProps {
  /**
   * Buildings the authenticated user may select.
   */
  buildings: SelectableBuilding[];
  /**
   * The currently selected building ID
   */
  currentBuildingId: number | null;
  /**
   * Whether the modal is opened (controlled from parent)
   */
  opened: boolean;
  /**
   * Callback to close the modal
   */
  onClose: () => void;
  /**
   * Callback invoked after the user chooses a building.
   */
  onSelect: (buildingId: number) => void;
  /**
   * Whether selecting a building is required before continuing.
   */
  required?: boolean;
}

/**
 * BuildingSelectorMenu component - displays a dropdown menu with accessible buildings
 */
const BuildingSelectorMenu: React.FC<BuildingSelectorMenuProps> = ({
  buildings,
  currentBuildingId,
  opened,
  onClose,
  onSelect,
  required = false,
}) => {
  const { _t } = useLang();

  return (
    <Modal
      variant="secondary"
      opened={opened}
      onClose={onClose}
      title={_t('Select building')}
      transitionProps={{ transition: 'slide-up', duration: 400 }}
      withCloseButton={!required}
      closeOnClickOutside={!required}
      closeOnEscape={!required}
    >
      <Box>
        <Box>
          {buildings
            .filter((building) => building.id !== undefined)
            .map((building) => (
              <Flex
                key={building.id}
                onClick={() => onSelect(building.id!)}
                align="center"
                gap="1rem"
                style={{
                  cursor: 'pointer',
                  padding: '0.75rem 0',
                }}
              >
                {/* Radio checkbox naľavo */}
                <Radio
                  checked={building.id === currentBuildingId}
                  aria-label={building.title || _t('Select building')}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => onSelect(building.id!)}
                  style={{ flexShrink: 0 }}
                />

                {/* Logo */}
                <Box
                  style={{
                    flexShrink: 0,
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {building.logoUrl ? (
                    <img
                      src={building.logoUrl}
                      alt={building.title || _t('Building logo')}
                      style={{ maxHeight: '48px', objectFit: 'contain' }}
                    />
                  ) : (
                    <HeaderIcon />
                  )}
                </Box>

                {/* Text vpravo */}
                <Box style={{ flex: 1, textAlign: 'right' }}>
                  <P1SemiBold
                    color={building.id === currentBuildingId ? theme.colors?.primary?.[9] : theme.colors?.gray?.[12]}
                  >
                    {building.title}
                  </P1SemiBold>
                </Box>
              </Flex>
            ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default BuildingSelectorMenu;
